import { useEffect, useRef, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  ADMIN_STATUS_EVENT,
  clearAdminStatusCache,
  fetchIsAdmin,
  isAdminUser,
} from '../utils/admin';
import { markInternalTraffic } from '../utils/ga';
import {
  ADMIN_VIEW_EVENT,
  ADMIN_VIEW_MEMBER,
  getAdminViewAs,
  isProspectPreview,
} from '../utils/adminView';
import {
  freeTrialExhausted,
  getLocalFreeMockUsed,
  getLocalFreeMockUsedForLevel,
  hasUsedFreeMockForLevel,
} from '../utils/access';
import {
  accessFlagsFromProfile,
  cacheEntitlementsForUser,
  readCachedEntitlementsForUser,
} from '../utils/entitlements';
import { bootstrapMonetizationEnabled, cacheMonetizationEnabled, loadSiteSettings } from '../utils/siteSettings';

function trialFields(profile = {}, user = auth.currentUser) {
  const usedL1 = user
    ? Boolean(profile.freeSessionUsed || profile.freeMockCompletedAt)
    : freeTrialExhausted(profile.freeTrialUsed, profile.freeSessionUsed, user, profile.freeMockCompletedAt);
  const usedL2 = hasUsedFreeMockForLevel('l2', profile, user);
  const usedL3 = hasUsedFreeMockForLevel('l3', profile, user);
  return {
    freeTrialUsedCount: usedL1 ? 1 : 0,
    freeTrialRemaining: usedL1 ? 0 : 1,
    freeUsed: usedL1,
    freeMockAvailable: !usedL1,
    freeMockCompletedAt: profile.freeMockCompletedAt || null,
    freeUsedL2: usedL2,
    freeUsedL3: usedL3,
    freeMockAvailableL2: !usedL2,
    freeMockAvailableL3: !usedL3,
  };
}

function buildAccess(user, profile, monetizationEnabled, viewAs, realAdmin, { accessUnknown = false } = {}) {
  const flags = accessFlagsFromProfile(profile || {});
  const asProspect = realAdmin && isProspectPreview(viewAs);
  // Prospect preview must look unpaid end to end. Leaking the admin's real entitlements made
  // EasyPay's ownsSku() treat an active L1 pass as owning monthly, hiding "Choose monthly".
  const owned = asProspect ? accessFlagsFromProfile({}) : flags;

  return {
    loading: false,
    user,
    /** True when the server says this account is an admin — Admin nav/console always shown. */
    realAdmin,
    /**
     * Practice / CTA bypass. Off in prospect preview so paywalls render.
     * On for admins in member view even without a paid pass.
     */
    isAdmin: realAdmin && !asProspect,
    /** Active L1 (or package), or admin in member view. */
    paid: asProspect ? false : flags.paid || realAdmin,
    paidUntil: owned.paidUntil,
    paidL2: asProspect ? false : flags.paidL2,
    paidL3: asProspect ? false : flags.paidL3,
    hasPackage: asProspect ? false : flags.hasPackage,
    entitlements: owned.entitlements,
    paidAmountUsd: owned.paidAmountUsd,
    paidAmountL2Usd: owned.paidAmountL2Usd,
    paidAmountL3Usd: owned.paidAmountL3Usd,
    lastSku: flags.lastSku,
    examDate: profile?.examDate || '',
    examWindowLabel: profile?.examWindowLabel || '',
    ...trialFields(profile || {}, user),
    monetizationEnabled,
    viewAs: realAdmin ? viewAs : ADMIN_VIEW_MEMBER,
    /**
     * CFA-38: profile read failed and no last-known entitlement cache.
     * UI must show retry — not paywall and not unlock paid mocks.
     */
    accessUnknown: !!accessUnknown,
  };
}

/**
 * CFA-38: when the live profile snapshot fails (quota / network / permission),
 * prefer last-known-good entitlements; otherwise mark access unknown.
 */
function accessFromProfileFailure(user, monetizationEnabled, viewAs, realAdmin) {
  const cached = readCachedEntitlementsForUser(user?.uid);
  if (cached) {
    return buildAccess(user, cached, monetizationEnabled, viewAs, realAdmin, { accessUnknown: false });
  }
  return buildAccess(user, {}, monetizationEnabled, viewAs, realAdmin, { accessUnknown: true });
}

export default function useAccess() {
  const currentUser = auth.currentUser;
  const initialAdmin = isAdminUser(currentUser);
  const profileRef = useRef({});
  const monetizationRef = useRef(bootstrapMonetizationEnabled());
  const userRef = useRef(currentUser);
  const realAdminRef = useRef(initialAdmin);
  const [retryNonce, setRetryNonce] = useState(0);
  const [viewAs, setViewAsState] = useState(() =>
    initialAdmin ? getAdminViewAs() : ADMIN_VIEW_MEMBER
  );
  const [state, setState] = useState(() => ({
    ...buildAccess(currentUser, {}, monetizationRef.current, viewAs, initialAdmin),
    loading: true,
    retryAccess: () => setRetryNonce((n) => n + 1),
  }));

  useEffect(() => {
    const syncView = () => setViewAsState(getAdminViewAs());
    window.addEventListener(ADMIN_VIEW_EVENT, syncView);
    window.addEventListener('storage', syncView);
    return () => {
      window.removeEventListener(ADMIN_VIEW_EVENT, syncView);
      window.removeEventListener('storage', syncView);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let unsubProfile = null;

    const attachRetry = (accessState) => ({
      ...accessState,
      retryAccess: () => setRetryNonce((n) => n + 1),
    });

    // Bound the initial auth wait, then give each signed-in profile its own deadline.
    let failSafe = setTimeout(() => {
      if (cancelled) return;
      const user = userRef.current;
      if (!user) {
        setState((prev) => (prev.loading ? { ...prev, loading: false } : prev));
        return;
      }
      const nextViewAs = realAdminRef.current ? getAdminViewAs() : ADMIN_VIEW_MEMBER;
      setState(attachRetry(accessFromProfileFailure(
        user, monetizationRef.current, nextViewAs, realAdminRef.current
      )));
    }, 2500);

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      clearTimeout(failSafe);
      if (unsubProfile) {
        unsubProfile();
        unsubProfile = null;
      }
      userRef.current = user;
      const authUid = user?.uid || null;

      // CFA-52: cached monetization immediately; site settings fetch in parallel (never blocks).
      let monetizationEnabled = bootstrapMonetizationEnabled();
      monetizationRef.current = monetizationEnabled;

      const stillCurrentAuth = () =>
        !cancelled && (userRef.current?.uid || null) === authUid;

      const applyMonetization = (enabled) => {
        if (!stillCurrentAuth()) return;
        monetizationEnabled = !!enabled;
        monetizationRef.current = monetizationEnabled;
        cacheMonetizationEnabled(monetizationEnabled);
        setState((prev) => {
          if (prev.loading) return prev;
          if (!!prev.monetizationEnabled === monetizationEnabled) return prev;
          return {
            ...prev,
            monetizationEnabled,
          };
        });
      };

      loadSiteSettings()
        .then((flags) => applyMonetization(!!flags.monetizationEnabled))
        .catch(() => {
          /* keep bootstrap / cached default */
        });

      if (!user) {
        clearAdminStatusCache();
        profileRef.current = {};
        realAdminRef.current = false;
        setState(
          attachRetry(buildAccess(null, {}, monetizationEnabled, ADMIN_VIEW_MEMBER, false))
        );
        return;
      }

      profileRef.current = {};
      // CFA-52: use cached admin (or treat as non-admin) immediately — do not block
      // publishReady on /api/admin-status. Admins still upgrade when the fetch resolves.
      const cachedAdmin = isAdminUser(user);
      realAdminRef.current = cachedAdmin;
      setState({
        ...buildAccess(user, {}, monetizationEnabled, ADMIN_VIEW_MEMBER, cachedAdmin),
        loading: true,
        accessUnknown: false,
        retryAccess: () => setRetryNonce((n) => n + 1),
      });

      let profileResolved = false;
      let latestRealAdmin = cachedAdmin;
      /** Set when the first profile outcome was a snapshot error (not empty doc). */
      let profileFailed = false;

      const stillCurrent = () =>
        !cancelled && (userRef.current?.uid || null) === user.uid;

      const publishReady = () => {
        if (!stillCurrent() || !profileResolved) return;
        const nextViewAs = latestRealAdmin ? getAdminViewAs() : ADMIN_VIEW_MEMBER;
        setViewAsState(nextViewAs);
        if (profileFailed) {
          setState(
            attachRetry(
              accessFromProfileFailure(user, monetizationRef.current, nextViewAs, latestRealAdmin)
            )
          );
          return;
        }
        setState(
          attachRetry(
            buildAccess(user, profileRef.current, monetizationRef.current, nextViewAs, latestRealAdmin)
          )
        );
      };

      const publishAdminUpdate = () => {
        if (!stillCurrent() || !profileResolved) return;
        const nextViewAs = latestRealAdmin ? getAdminViewAs() : ADMIN_VIEW_MEMBER;
        setViewAsState(nextViewAs);
        if (profileFailed) {
          setState(
            attachRetry(
              accessFromProfileFailure(user, monetizationRef.current, nextViewAs, latestRealAdmin)
            )
          );
          return;
        }
        setState(
          attachRetry(
            buildAccess(user, profileRef.current, monetizationRef.current, nextViewAs, latestRealAdmin)
          )
        );
      };

      failSafe = setTimeout(() => {
        if (!stillCurrent() || profileResolved) return;
        // Settle the same state as a snapshot error so late admin/profile updates
        // can still recover, without interpreting an unavailable profile as unpaid.
        profileFailed = true;
        profileResolved = true;
        publishReady();
      }, 2500);

      fetchIsAdmin(user).then((realAdmin) => {
        if (!stillCurrent()) return;
        latestRealAdmin = realAdmin === true;
        realAdminRef.current = latestRealAdmin;
        if (latestRealAdmin) markInternalTraffic();
        if (profileResolved) {
          publishAdminUpdate();
        }
      });

      try {
        unsubProfile = onSnapshot(
          doc(db, 'userProfiles', user.uid),
          (snap) => {
            if (!stillCurrent()) return;
            clearTimeout(failSafe);
            profileFailed = false;
            profileRef.current = snap.exists() ? snap.data() : {};
            cacheEntitlementsForUser(user.uid, profileRef.current);
            if (!profileResolved) {
              profileResolved = true;
              publishReady();
              return;
            }
            setState(
              attachRetry(
                buildAccess(
                  user,
                  profileRef.current,
                  monetizationRef.current,
                  getAdminViewAs(),
                  realAdminRef.current
                )
              )
            );
          },
          (err) => {
            console.error('Error listening to userProfile:', err);
            if (!stillCurrent()) return;
            clearTimeout(failSafe);
            profileFailed = true;
            if (!profileResolved) {
              profileResolved = true;
              publishReady();
              return;
            }
            // Mid-session error: keep last good entitlement; else unknown.
            setState((prev) => {
              if (prev.paid || prev.paidL2 || prev.paidL3 || prev.hasPackage || prev.isAdmin) {
                return { ...prev, loading: false, accessUnknown: false };
              }
              return attachRetry(
                accessFromProfileFailure(
                  user,
                  monetizationRef.current,
                  getAdminViewAs(),
                  realAdminRef.current
                )
              );
            });
          }
        );
      } catch (err) {
        if (stillCurrent()) {
          clearTimeout(failSafe);
          profileFailed = true;
          profileResolved = true;
          publishReady();
        }
      }
    });

    return () => {
      cancelled = true;
      clearTimeout(failSafe);
      if (unsubProfile) unsubProfile();
      unsubscribe();
    };
  }, [retryNonce]);

  useEffect(() => {
    const user = userRef.current;
    if (!user || !realAdminRef.current) return;
    setState((prev) => ({
      ...buildAccess(user, profileRef.current, monetizationRef.current, viewAs, true, {
        accessUnknown: prev.accessUnknown,
      }),
      loading: prev.loading,
      retryAccess: prev.retryAccess,
    }));
  }, [viewAs]);

  useEffect(() => {
    const onAdminStatus = () => {
      const user = userRef.current;
      if (!user?.uid) return;
      const realAdmin = isAdminUser(user);
      realAdminRef.current = realAdmin;
      setState((prev) => {
        // Ignore events that race past a signed-out / switched user.
        if (prev.user?.uid && prev.user.uid !== user.uid) return prev;
        return {
          ...buildAccess(user, profileRef.current, monetizationRef.current, getAdminViewAs(), realAdmin, {
            accessUnknown: prev.accessUnknown,
          }),
          loading: prev.loading,
          retryAccess: prev.retryAccess,
        };
      });
    };
    window.addEventListener(ADMIN_STATUS_EVENT, onAdminStatus);
    return () => window.removeEventListener(ADMIN_STATUS_EVENT, onAdminStatus);
  }, []);

  useEffect(() => {
    const refreshTrial = () => {
      setState((prev) => {
        if (prev.paid || prev.isAdmin) return prev;
        const usedL1 = prev.user
          ? Boolean(prev.freeUsed || prev.freeMockCompletedAt || getLocalFreeMockUsed())
          : getLocalFreeMockUsed();
        const usedL2 = prev.user
          ? Boolean(prev.freeUsedL2 || getLocalFreeMockUsedForLevel('l2'))
          : getLocalFreeMockUsedForLevel('l2');
        const usedL3 = prev.user
          ? Boolean(prev.freeUsedL3 || getLocalFreeMockUsedForLevel('l3'))
          : getLocalFreeMockUsedForLevel('l3');
        return {
          ...prev,
          freeTrialUsedCount: usedL1 ? 1 : 0,
          freeTrialRemaining: usedL1 ? 0 : 1,
          freeUsed: usedL1,
          freeMockAvailable: !usedL1,
          freeUsedL2: usedL2,
          freeUsedL3: usedL3,
          freeMockAvailableL2: !usedL2,
          freeMockAvailableL3: !usedL3,
        };
      });
    };
    window.addEventListener('storage', refreshTrial);
    window.addEventListener('cfa-free-trial-used', refreshTrial);
    window.addEventListener('cfa-free-session-used', refreshTrial);
    return () => {
      window.removeEventListener('storage', refreshTrial);
      window.removeEventListener('cfa-free-trial-used', refreshTrial);
      window.removeEventListener('cfa-free-session-used', refreshTrial);
    };
  }, []);

  return state;
}
